/**
 * Browser-side .docx preparer
 * - Extracts images from word/media/, uploads them, gets permanent URLs in document order
 * - Uploads the .docx itself so the backend can send it to GPT-4o for proper text/layout extraction
 */
import { uploadFile } from 'zite-file-upload-sdk';

export interface DocxPreparation {
  docxUrl: string;
  imageUrls: string[];   // in document appearance order — matches [IMAGE:N] from GPT-4o
  imageCount: number;
}

// ─── ZIP ─────────────────────────────────────────────────────────────────────

async function inflate(raw: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const ds = new DecompressionStream('deflate-raw');
  const w = ds.writable.getWriter();
  const r = ds.readable.getReader();
  w.write(raw);
  w.close();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  for (;;) {
    const { value, done } = await r.read();
    if (done) break;
    if (value) chunks.push(value as Uint8Array<ArrayBuffer>);
  }
  const total = chunks.reduce((s, c) => s + c.length, 0);
  const out = new Uint8Array(total) as Uint8Array<ArrayBuffer>;
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}

interface ZipFile { name: string; data: Uint8Array<ArrayBuffer> }

async function readZip(buf: ArrayBuffer): Promise<ZipFile[]> {
  const v = new DataView(buf);
  const len = new Uint8Array(buf).length;
  const files: ZipFile[] = [];

  let eocd = -1;
  for (let i = len - 22; i >= Math.max(0, len - 65558); i--) {
    if (v.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('Not a valid DOCX/ZIP file');

  const cdOff = v.getUint32(eocd + 16, true);
  const cdSize = v.getUint32(eocd + 12, true);
  let p = cdOff;

  while (p < cdOff + cdSize) {
    if (v.getUint32(p, true) !== 0x02014b50) break;
    const method = v.getUint16(p + 10, true);
    const cSize  = v.getUint32(p + 20, true);
    const fnLen  = v.getUint16(p + 28, true);
    const exLen  = v.getUint16(p + 30, true);
    const cmLen  = v.getUint16(p + 32, true);
    const lhOff  = v.getUint32(p + 42, true);
    const name   = new TextDecoder().decode(new Uint8Array(buf, p + 46, fnLen));
    const dOff   = lhOff + 30 + v.getUint16(lhOff + 26, true) + v.getUint16(lhOff + 28, true);
    const raw    = new Uint8Array(buf, dOff, cSize) as Uint8Array<ArrayBuffer>;

    let data: Uint8Array<ArrayBuffer> = new Uint8Array(0) as Uint8Array<ArrayBuffer>;
    try {
      if (method === 0)      data = raw.slice() as Uint8Array<ArrayBuffer>;
      else if (method === 8) data = await inflate(raw.slice() as Uint8Array<ArrayBuffer>);
    } catch { /* skip corrupt entries */ }

    if (name && data.length > 0) files.push({ name, data });
    p += 46 + fnLen + exLen + cmLen;
  }
  return files;
}

const WEB_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']);
const EXT_MIME: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
  gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp',
};

// ─── Public API ──────────────────────────────────────────────────────────────

export async function parseDocx(
  file: File,
  onProgress?: (msg: string) => void,
): Promise<DocxPreparation> {
  onProgress?.('Reading document…');
  const buf = await file.arrayBuffer();

  onProgress?.('Unpacking ZIP…');
  const zip = await readZip(buf);

  const relsFile = zip.find(f => f.name === 'word/_rels/document.xml.rels');
  const docFile  = zip.find(f => f.name === 'word/document.xml');
  const imgFiles = zip.filter(f => {
    if (!f.name.startsWith('word/media/')) return false;
    return WEB_EXT.has(f.name.split('.').pop()?.toLowerCase() ?? '');
  });

  // Map rId → file path
  const rIdToPath: Record<string, string> = {};
  if (relsFile) {
    const xml = new TextDecoder().decode(relsFile.data);
    const re  = /Id="([^"]+)"[^>]*Target="([^"]+)"/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(xml)) !== null) {
      const target = m[2].replace(/^\.\//, '').replace(/^\//, '');
      rIdToPath[m[1]] = `word/${target}`;
    }
  }

  // Upload images
  if (imgFiles.length > 0) onProgress?.(`Uploading ${imgFiles.length} image${imgFiles.length !== 1 ? 's' : ''}…`);
  const pathToUrl: Record<string, string> = {};
  for (const img of imgFiles) {
    try {
      const ext  = img.name.split('.').pop()?.toLowerCase() ?? 'png';
      const blob = new Blob([img.data], { type: EXT_MIME[ext] ?? 'image/png' });
      const { fileUrl } = await uploadFile({ data: blob, filename: img.name.split('/').pop() ?? 'image.png' });
      pathToUrl[img.name] = fileUrl;
    } catch (e) { console.warn('Could not upload image', img.name, e); }
  }

  // Build rId → URL map
  const rIdToUrl: Record<string, string> = {};
  for (const [id, path] of Object.entries(rIdToPath)) {
    if (pathToUrl[path]) rIdToUrl[id] = pathToUrl[path];
  }

  // Determine image order by scanning document.xml for r:embed in document order
  const orderedUrls: string[] = [];
  const seenPaths = new Set<string>();
  if (docFile) {
    const docXml = new TextDecoder().decode(docFile.data);
    const re = /r:embed="([^"]+)"/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(docXml)) !== null) {
      const url = rIdToUrl[m[1]];
      if (url && !seenPaths.has(url)) {
        seenPaths.add(url);
        orderedUrls.push(url);
      }
    }
  }

  // Upload the .docx itself
  onProgress?.('Uploading document…');
  const { fileUrl: docxUrl } = await uploadFile({ data: file, filename: file.name });

  return { docxUrl, imageUrls: orderedUrls, imageCount: orderedUrls.length };
}
